import {Button, Flag, Separator} from 'platform/components';
import {Box, Clickable, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {EMPTY_PLACEHOLDER, suffixTestId, TestIdProps} from 'shared/web';

import {useCatalogue} from '../../../hooks/useCatalogue';
import {useVehicleTranslatedInfo} from '../../../hooks/useVehicleTranslatedInfo';
import i18n from '../../../i18n/i18n';
import {useResolveVehicleWarningsMutation} from '../../../store/importApi';
import {RowStatus} from '../../../types/api/api';
import {handleApiError} from '../../../utils/handleApiError';
import {FeatureListRow} from '../../AdvertisementPage/components/FeatureListRow';
import {VEHICLETYPE_PASSENGER_CAR} from '../../Create/constants/vehiclePassengerCar';
import {ImportVehicle} from '../types/ImportVehicle';
import {attributeToRow} from '../utils/attributeToRow';
import {translateAttributeLabel} from '../utils/translateAttributeLabel';

interface VehicleImportRowProps extends TestIdProps {
  batchId: string;
  vehicle: ImportVehicle;
  isExpanded: boolean;
  onToggle: () => void;
  onEditSpecification: () => void;
  onEditEquipment: () => void;
}

export function VehicleImportRow(props: VehicleImportRowProps) {
  const catalogue = useCatalogue();
  const [resolveVehicleWarnings, {isLoading: isResolveLoading}] =
    useResolveVehicleWarningsMutation();
  const {data: translatedInfo} = useVehicleTranslatedInfo(
    props.vehicle.make != null
      ? {
          make: props.vehicle.make,
          modelFamily: props.vehicle.model ?? undefined,
          vehicleType: VEHICLETYPE_PASSENGER_CAR,
        }
      : null
  );
  const makePart = translatedInfo?.make ?? props.vehicle.make ?? '';
  const modelPart = translatedInfo?.model ?? props.vehicle.model ?? '';
  const hasMakeOrModel = makePart.trim() !== '' || modelPart.trim() !== '';
  const displayNameRaw = hasMakeOrModel
    ? `${makePart} ${modelPart}`.trim() +
      (isNotNil(props.vehicle.year) ? ` ${props.vehicle.year}` : '')
    : props.vehicle.name;
  const displayName = displayNameRaw.trim() === '' ? EMPTY_PLACEHOLDER : displayNameRaw;

  const displaySpecs = (() => {
    const v = props.vehicle;
    const parts: string[] = [];
    if (v.power != null) {
      parts.push(`${v.power} kW`);
    }
    if (v.transmission) {
      parts.push(catalogue.getTransmission(v.transmission));
    }
    if (v.drive) {
      parts.push(catalogue.getDrive(v.drive));
    }
    if (v.fuel) {
      parts.push(catalogue.getFuelType(v.fuel));
    }
    if (v.mileage != null) {
      parts.push(`${v.mileage.toLocaleString()} km`);
    }
    return parts.length > 0 ? parts.join(' • ') : v.specs;
  })();

  return (
    <StyledRow $rowStatus={props.vehicle.rowStatus} data-testid={suffixTestId('row', props)}>
      <StyledIndicator $rowStatus={props.vehicle.rowStatus} />
      <Clickable onClick={props.onToggle} data-testid={suffixTestId('rowHeader', props)}>
        <Box paddingLeft={4} paddingRight={4} paddingTop={2} paddingBottom={2}>
          <HStack justify="space-between" align="center">
            <HStack spacing={4} align="center">
              <Text size="xSmall" color="secondary" noWrap>
                {props.vehicle.nr}
              </Text>
              <VStack spacing={1}>
                <Text size="base" color="primary">
                  {displayName}
                </Text>
                <HStack spacing={2} align="center">
                  <Show when={displaySpecs.trim() !== ''}>
                    <Text size="xSmall" color="secondary">
                      {displaySpecs}
                      {props.vehicle.hasVat ? ' • ' : null}
                    </Text>
                  </Show>
                  {props.vehicle.hasVat && (
                    <Flag label={i18n.t('general.labels.vat')} size="small" colorScheme="black" />
                  )}
                </HStack>
              </VStack>
            </HStack>
            <Icon
              value={props.isExpanded ? 'navigation/expand_less' : 'navigation/expand_more'}
              size={6}
              color="text.secondary"
            />
          </HStack>
        </Box>
      </Clickable>

      {props.isExpanded && (
        <Box paddingLeft={4} paddingRight={4} paddingBottom={4}>
          <VStack spacing={4}>
            {props.vehicle.features.length > 0 && (
              <FeatureListRow
                values={props.vehicle.features}
                spacing={2}
                textColor="secondary"
                data-testid={suffixTestId('featuresRow', props)}
              />
            )}

            <HStack spacing={3}>
              <Button
                variant="secondary"
                leftIcon="automotive/car"
                title={i18n.t('page.import.actions.editSpecification')}
                onClick={props.onEditSpecification}
                data-testid={suffixTestId('editSpecButton', props)}
              />
              <Button
                variant="secondary"
                leftIcon="content/create"
                title={i18n.t('page.import.actions.editEquipment')}
                onClick={props.onEditEquipment}
                data-testid={suffixTestId('editEquipmentButton', props)}
              />
              <Show when={props.vehicle.unresolvedWarnings}>
                <Button
                  variant="warning"
                  leftIcon="action/check_circle"
                  title={i18n.t('page.import.actions.confirmSpecificationOk')}
                  isLoading={isResolveLoading}
                  onClick={() =>
                    resolveVehicleWarnings({
                      batchId: props.batchId,
                      vehicleId: props.vehicle.id,
                    })
                      .unwrap()
                      .catch(handleApiError)
                  }
                  data-testid={suffixTestId('confirmSpecOkButton', props)}
                />
              </Show>
            </HStack>

            {props.vehicle.attributes.length > 0 && (
              <VStack spacing={2}>
                <Separator />
                <Text size="xSmall" color="secondary">
                  {i18n.t('page.import.labels.originalValues')}
                </Text>
                <AttributesColumnFlow data-testid={suffixTestId('attributes', props)}>
                  {props.vehicle.attributes.map((attr) => {
                    const row = attributeToRow(attr, translateAttributeLabel);
                    return (
                      <Box
                        key={`${attr.label}\0${attr.value}`}
                        paddingVertical={2}
                        borderColor="palettes.neutral.30.100"
                        borderBottom="1px solid"
                      >
                        <HStack spacing={4}>
                          <Box width="50%">
                            <Text size="xSmall" color="secondary">
                              {row.label}
                            </Text>
                          </Box>
                          <Box flex={1} minWidth={0}>
                            {row.content}
                          </Box>
                        </HStack>
                      </Box>
                    );
                  })}
                </AttributesColumnFlow>
              </VStack>
            )}
          </VStack>
        </Box>
      )}
    </StyledRow>
  );
}

const StyledRow = styled.div<{$rowStatus: RowStatus}>`
  position: relative;
  border-bottom: 1px solid ${({theme}) => theme.colors.general.separator};
  background: ${({theme, $rowStatus}) =>
    match($rowStatus)
      .with('error', () => theme.colors.palettes.red[10][100])
      .with('warning', () => theme.colors.palettes.orange[10][100])
      .with('success', () => theme.colors.palettes.white[10][100])
      .exhaustive()};
`;

const AttributesColumnFlow = styled.div`
  column-count: 3;
  column-gap: ${({theme}) => theme.getSize(4)};
  width: 100%;

  @media ${({theme}) => theme.devices.mobile} {
    column-count: 1;
  }

  @media ${({theme}) => theme.devices.tablet} {
    column-count: 2;
  }
`;

const StyledIndicator = styled.div<{$rowStatus: RowStatus}>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${({theme}) => theme.getSize(1)};
  background: ${({theme, $rowStatus}) =>
    match($rowStatus)
      .with('error', () => theme.colors.severity.danger)
      .with('warning', () => theme.colors.severity.warning)
      .with('success', () => theme.colors.palettes.green[60][100])
      .exhaustive()};
`;
